/**
 * Utility to parse Pydantic validation and API errors into user-friendly messages.
 */

export const parseErrorMessage = (
  errorData: any,
  fallbackMessage: string = "An unexpected error occurred"
): string => {
  if (!errorData) return fallbackMessage;

  // If the errorData is already a string, return it
  if (typeof errorData === 'string') {
    return errorData;
  }

  // Helper to parse validation error array
  const parseValidationErrors = (errors: any[]): string => {
    const validationErrors = errors.map((err: any) => {
      // Extract field name from loc, e.g., ["body", "phone_no"] -> "phone_no"
      const field = err.loc && err.loc.length > 1 ? err.loc[1] : 'unknown field';
      
      // Check if field is a phone number field (case-insensitive check)
      const isPhoneField = ['phone_no', 'phone', 'mobile_number', 'mobile'].includes(String(field).toLowerCase());
      if (isPhoneField) {
        return 'Enter valid phone number';
      }

      // Return a capitalized and formatted error description for other fields
      const friendlyField = String(field)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, (c: string) => c.toUpperCase());

      let msg = err.msg || 'Invalid value';
      
      // Clean up value_error. prefix
      msg = msg.replace(/value_error\./g, '');
      
      // Map technical terms in the message body
      msg = msg
        .replace(/email_address/g, 'email address')
        .replace(/mobile_number/g, 'mobile number')
        .replace(/user_name/g, 'user name');

      // Specific validation message enhancements
      if (msg.includes('Password must contain at least one uppercase letter')) {
        msg = msg.replace('Password must contain at least one uppercase letter', 'Password must contain at least one uppercase letter (A-Z)');
      }
      if (msg.includes('Invalid mobile number format')) {
        msg = msg.replace('Invalid mobile number format', 'Mobile number must be in Australian format: +61xxxxxxxx or 0xxxxxxxxx');
      }
      
      return `${friendlyField}: ${msg}`;
    });

    // Filter out duplicate identical messages
    const uniqueErrors = Array.from(new Set(validationErrors));
    return uniqueErrors.join(', ');
  };

  if (Array.isArray(errorData)) {
    return parseValidationErrors(errorData);
  }

  // Handle case where we have a nested detail property
  if (errorData.detail) {
    const detail = errorData.detail;

    if (typeof detail === 'string') {
      return detail;
    }

    if (Array.isArray(detail)) {
      return parseValidationErrors(detail);
    }

    if (typeof detail === 'object' && detail !== null) {
      if (detail.message) {
        return detail.message;
      }
      if (detail.errors && Array.isArray(detail.errors)) {
        return detail.errors.join('\n');
      }
    }
  }

  if (errorData.message && typeof errorData.message === 'string') {
    return errorData.message;
  }

  return fallbackMessage;
};
